import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Paperclip, Upload, Trash2, Download } from "lucide-react";

// SA-5: attachments for Sales Orders + PRFs. `collection` is the URL segment
// ("sales-orders" | "project-registrations"). Files persist locally and mirror to S3.
const KINDS = [
    ["card_deposit_slip", "Card / Deposit slip"],
    ["bdo_qr_ref", "BDO QR PH ref"],
    ["gcash_ref", "G-Cash ref"],
    ["sales_quotation", "Sales Quotation"],
    ["mother_po", "Mother PO"],
    ["sales_agreement", "Sales Agreement"],
    ["collection_date", "Collection Date"],
    ["delivery_timeline", "Delivery Timeline"],
    ["other", "Other"],
];
const kindLabel = (k, list = KINDS) => (list.find((x) => x[0] === k) || [null, k])[1];
const fmtSize = (n) => {
    const b = Number(n || 0);
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1048576).toFixed(1)} MB`;
};

export default function Attachments({ collection, docId, editable, kinds, defaultKind, displayKinds, onlyKinds }) {
    const KIND_LIST = kinds || KINDS;           // kinds the user may UPLOAD (the dropdown)
    const LABEL_LIST = displayKinds || KIND_LIST; // kinds used to LABEL existing files
    const [items, setItems] = useState([]);
    const [kind, setKind] = useState(defaultKind || KIND_LIST[0][0]);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/${collection}/${docId}/attachments`);
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            /* silent: list is best-effort */
        }
    };
    useEffect(() => { if (docId) load(); }, [collection, docId]);

    const upload = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        await api.post(`/${collection}/${docId}/attachments`, fd);
    };

    // Uploads every selected file, one request per file (the backend takes one file per POST),
    // in the order chosen -- so picking several return-item photos at once needs one click, not
    // one per photo. Reports what succeeded and what didn't rather than stopping at the first.
    const uploadMany = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setBusy(true);
        let ok = 0;
        const failed = [];
        for (const f of files) {
            try { await upload(f); ok += 1; }
            catch (e) { failed.push(`${f.name}: ${formatApiError(e)}`); }
        }
        if (fileRef.current) fileRef.current.value = "";
        if (ok) toast.success(files.length > 1 ? `${ok} of ${files.length} attachment(s) uploaded` : "Attachment uploaded");
        failed.forEach((msg) => toast.error(msg));
        await load();
        setBusy(false);
    };

    const remove = async (aid) => {
        if (!window.confirm("Remove this attachment?")) return;
        setBusy(true);
        try {
            await api.delete(`/${collection}/${docId}/attachments/${aid}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    // onlyKinds (optional): show ONLY attachments of these kinds in this instance (UAT 10-26) --
    // keeps the agent's for-assessment photos out of the reviewer's Received-RIW section, and vice versa.
    const shown = onlyKinds ? items.filter((a) => onlyKinds.includes(a.kind)) : items;
    return (
        <div className="sc-card p-4 space-y-3" data-testid="attachments-card">
            <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-muted-foreground" />
                <div className="sc-section-title">Attachments</div>
            </div>

            {editable && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <select className="sc-input h-9 sm:w-52" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="attach-kind">
                        {KIND_LIST.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        onChange={(e) => uploadMany(e.target.files)}
                        disabled={busy}
                        className="text-sm flex-1"
                        data-testid="attach-file"
                    />
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Upload className="w-3 h-3" /> max 15MB each, select several at once</span>
                </div>
            )}

            {shown.length === 0 ? (
                <div className="text-xs text-muted-foreground">No attachments yet.</div>
            ) : (
                <ul className="divide-y divide-border">
                    {shown.map((a) => (
                        <li key={a.id} className="py-2 flex items-center gap-3 text-sm">
                            <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border bg-secondary/40 whitespace-nowrap">{kindLabel(a.kind, LABEL_LIST)}</span>
                            <a href={a.download_url} target="_blank" rel="noreferrer" className="flex-1 truncate hover:underline inline-flex items-center gap-1" title={a.orig_name}>
                                <Download className="w-3.5 h-3.5 shrink-0" /> {a.orig_name}
                            </a>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtSize(a.size)}</span>
                            <span className="text-xs text-muted-foreground hidden md:inline whitespace-nowrap">{a.uploaded_by}</span>
                            {editable && (
                                <button onClick={() => remove(a.id)} disabled={busy} className="h-7 w-7 grid place-items-center rounded border border-border hover:bg-secondary" title="Remove">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
